import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePrompts } from '../contexts/PromptContext';
import { Prompt } from '../types';
import { PROMPT_CATEGORIES } from '../utils/constants';
import { uploadApi } from '../services/api';
import toast from 'react-hot-toast';

interface EditPromptModalProps {
  prompt: Prompt | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ImagePreview {
  file?: File;
  preview: string;
  uploading?: boolean;
  uploaded?: boolean;
  url?: string;
  error?: string;
  isExisting?: boolean; // 标记是否是已存在的图片
}

const EditPromptModal: React.FC<EditPromptModalProps> = ({ prompt, isOpen, onClose }) => {
  const { updatePrompt } = usePrompts();
  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '其他',
    tags: '',
    description: '',
    changeDescription: '',
    price: 0
  });

  const MAX_IMAGES = 9;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  // 当prompt变化时更新表单数据
  useEffect(() => {
    if (prompt) {
      setFormData({
        title: prompt.title,
        content: prompt.content,
        category: prompt.category || '其他',
        tags: prompt.tags.join(', '),
        description: prompt.description || '',
        changeDescription: '',
        price: prompt.price || 0
      });
      // 加载已有的图片
      if (prompt.images && prompt.images.length > 0) {
        const existingImages: ImagePreview[] = prompt.images.map(url => ({
          preview: url,
          url: url,
          uploaded: true,
          isExisting: true
        }));
        setImages(existingImages);
      } else {
        setImages([]);
      }
    }
  }, [prompt]);

  // 处理文件
  const processFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const remainingSlots = MAX_IMAGES - images.length;
    if (remainingSlots <= 0) {
      toast.error(`最多只能上传${MAX_IMAGES}张图片`);
      return;
    }

    const newImages: ImagePreview[] = [];
    const errors: string[] = [];

    fileArray.slice(0, remainingSlots).forEach(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: 不支持的图片格式`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: 文件大小超过5MB`);
        return;
      }
      const preview = URL.createObjectURL(file);
      newImages.push({ file, preview });
    });

    if (errors.length > 0) {
      toast.error(errors.join('\n'));
    }

    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages]);
    }
  };

  // 拖拽事件
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => {
      const newImages = [...prev];
      const removed = newImages.splice(index, 1)[0];
      if (removed.preview && !removed.isExisting) {
        URL.revokeObjectURL(removed.preview);
      }
      return newImages;
    });
  };

  // 上传新增的图片
  const uploadNewImages = async (): Promise<string[]> => {
    const imagesToUpload = images.filter(img => img.file && !img.uploaded);
    const uploadedUrls: string[] = [];

    for (let i = 0; i < imagesToUpload.length; i++) {
      const imageData = imagesToUpload[i];
      if (!imageData.file) continue;

      const index = images.findIndex(img => img === imageData);
      setImages(prev => {
        const newImages = [...prev];
        newImages[index] = { ...newImages[index], uploading: true };
        return newImages;
      });

      try {
        const result = await uploadApi.uploadImage(imageData.file);
        if (result.success && result.data?.url) {
          const uploadedUrl = result.data.url;
          uploadedUrls.push(uploadedUrl);
          setImages(prev => {
            const newImages = [...prev];
            newImages[index] = { ...newImages[index], uploading: false, uploaded: true, url: uploadedUrl };
            return newImages;
          });
        }
      } catch (error) {
        setImages(prev => {
          const newImages = [...prev];
          newImages[index] = { ...newImages[index], uploading: false, error: '上传失败' };
          return newImages;
        });
        throw error;
      }
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!prompt || !updatePrompt) return;

    // 验证价格
    if (formData.price < 0 || formData.price > 1000) {
      toast.error('价格必须在0-1000元之间');
      return;
    }

    // 验证付费提示词必须填写介绍
    if (formData.price > 0 && !formData.description.trim()) {
      toast.error('付费提示词必须填写提示词介绍');
      return;
    }

    setIsLoading(true);

    try {
      // 上传新图片
      let allImageUrls: string[] = [];
      
      // 保留已存在的图片URL
      const existingUrls = images.filter(img => img.isExisting && img.url).map(img => img.url!);
      allImageUrls = [...existingUrls];

      // 上传新图片
      const newImageCount = images.filter(img => img.file && !img.uploaded).length;
      if (newImageCount > 0) {
        toast.loading('正在上传图片...', { id: 'upload-images' });
        const newUrls = await uploadNewImages();
        allImageUrls = [...allImageUrls, ...newUrls];
        toast.success(`成功上传 ${newUrls.length} 张图片`, { id: 'upload-images' });
      }

    const tagsArray = formData.tags 
      ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) 
      : [];

    const updateData = {
      title: formData.title,
      content: formData.content,
      category: formData.category,
      tags: tagsArray,
        description: formData.price > 0 ? formData.description : '',
        images: allImageUrls,
        price: formData.price,
      changeDescription: formData.changeDescription || '提示词编辑更新'
    };

      await updatePrompt(prompt.id, updateData);
      onClose();
    } catch (error) {
      // 错误已在context中处理
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!prompt) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-200"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">编辑提示词</h3>
              <button
                onClick={onClose}
                title="关闭"
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">标题</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="给您的提示词起个名字"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    分类 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    title="选择提示词分类"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {PROMPT_CATEGORIES.filter(cat => cat !== '其他').map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">标签</label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="用逗号分隔，如：写作,创意"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-tag mr-1 text-emerald-500"></i>
                    价格
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400 mr-2">¥</span>
                    <input
                      type="number"
                      name="price"
                      min="0"
                      max="1000"
                      step="0.01"
                      value={formData.price}
                      onChange={handleInputChange}
                      className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">内容</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  required
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="输入您的提示词内容..."
                />
              </div>

              {/* 提示词介绍 - 仅当价格>0时显示 */}
              {formData.price > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-info-circle mr-2 text-amber-500"></i>
                    提示词介绍 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="简要介绍这个提示词的功能和使用场景，吸引用户购买..."
                  />
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    <i className="fas fa-lightbulb mr-1"></i>
                    付费提示词在广场展示介绍内容，用户购买后才能看到完整内容
                  </p>
                </div>
              )}

              {/* 图片上传区域 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <i className="fas fa-images mr-2 text-primary"></i>
                  效果展示图片 (可选，最多9张)
                </label>
                <div
                  className={`border-2 border-dashed rounded-xl p-4 transition-colors ${
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-300 dark:border-gray-600 hover:border-primary'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {images.length > 0 ? (
                    <div className="grid grid-cols-3 gap-3">
                      {images.map((img, index) => (
                        <div key={index} className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                          <img
                            src={img.preview}
                            alt={`预览 ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {img.uploading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <i className="fas fa-spinner fa-spin text-white text-xl"></i>
                            </div>
                          )}
                          {img.error && (
                            <div className="absolute inset-0 bg-red-500/50 flex items-center justify-center">
                              <i className="fas fa-exclamation-triangle text-white"></i>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                          >
                            <i className="fas fa-times text-xs"></i>
                          </button>
                          {img.isExisting && (
                            <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-green-500 text-white text-xs rounded">
                              已保存
                            </div>
                          )}
                        </div>
                      ))}
                      {images.length < MAX_IMAGES && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-video rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
                        >
                          <i className="fas fa-plus text-gray-400 text-xl mb-1"></i>
                          <span className="text-xs text-gray-500">添加图片</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      className="text-center py-6 cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        拖拽图片到这里，或点击上传
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        支持 JPG、PNG、GIF、WebP，单张最大 5MB
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <i className="fas fa-edit mr-2 text-primary"></i>
                  修改说明 (可选)
                </label>
                <input
                  type="text"
                  name="changeDescription"
                  value={formData.changeDescription}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="描述本次修改的内容，如：优化了逻辑结构、添加了新的示例等..."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  此说明将记录在版本历史中，帮助您追踪每次修改的目的
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-primary hover:bg-primary-dark text-white py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 primary-glow"
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      更新中...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      保存更改
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditPromptModal; 